import { useCallback, useEffect, useRef } from "react";

type Corner = "TL" | "TR" | "BL" | "BR";
const cornerSize = 100;
const defaultInactivityTime = 1500;

/**
 * useKnockCode will call a function if the user taps or clicks on the
 * specified corners of the screen in the correct order.
 */
export default function useKnockCode(
  secretPattern: Corner[],
  onSuccess: () => void,
  options?: {
    inactivityTime?: number;
  }
) {
  const { inactivityTime } = Object.assign(
    { inactivityTime: defaultInactivityTime },
    options
  );

  const knockSequenceRef = useRef<Corner[]>([]);

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  });

  const detectCorner = (
    x: number,
    y: number,
    width: number,
    height: number
  ) => {
    if (x < cornerSize && y < cornerSize) return "TL";
    if (x > width - cornerSize && y < cornerSize) return "TR";
    if (x < cornerSize && y > height - cornerSize) return "BL";
    if (x > width - cornerSize && y > height - cornerSize) return "BR";
    return null;
  };

  const handlePress = useCallback(
    (e: MouseEvent) => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        knockSequenceRef.current = [];
      }, inactivityTime);

      const corner = detectCorner(
        e.clientX,
        e.clientY,
        window.innerWidth,
        window.innerHeight
      );

      // if it matches the next step in the sequence
      if (corner === secretPattern[knockSequenceRef.current.length]) {
        knockSequenceRef.current.push(corner);
        // if the sequence is complete, call the callback
        if (knockSequenceRef.current.length === secretPattern.length) {
          onSuccess();
          knockSequenceRef.current = [];
          return;
        }
      } else {
        // if the key doesn't match, reset the sequence
        knockSequenceRef.current = [];
      }
    },
    [secretPattern, onSuccess, inactivityTime]
  );

  // NOTE: touch devices will have a 300ms delay before
  // firing the mousedown event. however we do not want
  // to optimize because a) we don't want to detect if
  // we're using a touch device and b) we don't want to
  // prevent the default behavior of touching the screen.
  useEffect(() => {
    window.addEventListener("mousedown", handlePress);
    return () => {
      window.removeEventListener("mousedown", handlePress);
    };
  }, [handlePress]);
}
